/** @jsxRuntime classic */
/** @jsx jsx */
import { useState } from 'react';
import { css, jsx } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import Breadcrumbs from '@mui/material/Breadcrumbs';
import { Link } from 'react-router-dom';
import Text from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Skeleton from '@mui/material/Skeleton';

import { useStore, INPUT_FOCUS } from '../../store';

function noop() {}

export default function FolderBreadcrumbs({
  workspace,
  currentFolder,
  transcriptName,
  editable,
  onNameChange = noop,
  onClick = noop,
  maxItems,
  maxWidth,
  showParents = true,
  loading = false,
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [, setFocus] = useStore(INPUT_FOCUS);

  const parents = currentFolder?.parents || [];

  const leaf = parents[parents.length - 1];

  return (
    <Breadcrumbs
      aria-label="breadcrumb"
      maxItems={maxItems}
      css={css`
        display: flex;
      `}
    >
      {showParents && workspace?.name ? (
        <Link
          to="/"
          css={css`
            text-decoration: none;
            color: ${currentFolder === '/' ? 'black' : 'var(--text-secondary)'};
          `}
          onClick={(e) => onClick(e, workspace?.rootFolder?.folder_prefix)}
        >
          <Text
            variant="h6"
            fontWeight="bold"
            css={css`
              max-width: ${maxWidth};
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            `}
          >
            {workspace?.name}
          </Text>
        </Link>
      ) : loading ? (
        <Skeleton
          variant="text"
          width="100px"
          css={css`
            height: 32px;
          `}
        />
      ) : null}
      {(showParents ? parents : []).slice(1).map((parent, index) => (
        <Link
          key={parent.id}
          to={
            parent.folder_prefix !== '/'
              ? `/folders${parent?.folder_prefix}${parent?.id}`
              : '/'
          }
          css={css`
            text-decoration: none;
            color: ${leaf?.id !== parent?.id || transcriptName?.length
              ? 'var(--text-secondary)'
              : 'var(--text-primary)'};
          `}
          onClick={(e) => onClick(e, `${parent.folder_prefix}${parent.id}`)}
        >
          <Text
            variant="h6"
            fontWeight="bold"
            css={css`
              max-width: ${maxWidth};
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            `}
          >
            {parent.name}
          </Text>
        </Link>
      ))}
      {transcriptName && (
        <div
          css={css`
            display: flex;
            align-items: center;
            max-width: 20rem;
            width: 100%;
          `}
        >
          {!editing && (
            <Text
              variant="h6"
              fontWeight="bold"
              color="var(--text-primary)"
              noWrap
              css={css`
                text-overflow: ellipsis;
                overflow: hidden;
                max-width: ${maxWidth};
              `}
            >
              {transcriptName || t('Namnlös')}
            </Text>
          )}
          {editable && !editing && (
            <IconButton
              size="small"
              variant="contained"
              onClick={() => setEditing(true)}
            >
              <EditIcon />
            </IconButton>
          )}
          {editing && (
            <TextField
              size="small"
              variant="outlined"
              value={transcriptName}
              onChange={(e) => onNameChange(e.target.value)}
              onBlur={() => {
                setEditing(false);
                setFocus(false);
              }}
              onFocus={() => setFocus(true)}
              autoFocus
              fullWidth
              css={css`
                width: 20rem;
              `}
            />
          )}
        </div>
      )}
      {loading && (
        <Skeleton
          variant="text"
          width="100px"
          css={css`
            height: 32px;
          `}
        />
      )}
    </Breadcrumbs>
  );
}
